/**
 * Section behavior mixin for RfForm
 */
export default {

  // ////////////////////////////////////////////////////
  // Hooks
  // ////////////////////////////////////////////////////

  mounted () {
    if (this.sections.length) {
      // Push initial history state
      if (typeof history.replaceState !== 'undefined') {
        history.replaceState({ targetSectionIndex: 0 }, '', window.location.href)
      }

      // Listen for history state change and load correct section
      window.onpopstate = function (e) {
        if (e.state !== null && typeof e.state.targetSectionIndex !== 'undefined' && this.activeSectionIndex !== null) {
          if (this.activeSectionIndex > e.state.targetSectionIndex) {
            this.$emit('section:nav-prev', this.sections[e.state.targetSectionIndex], e)
          } else {
            this.$emit('section:nav-next', this.sections[e.state.targetSectionIndex], e)
          }
        }
      }.bind(this)
    }
  },

  // ////////////////////////////////////////////////////
  // Computed
  // ////////////////////////////////////////////////////

  computed: {
    activeSection () {
      return this.sections[this.activeSectionIndex]
    }
  },

  // ////////////////////////////////////////////////////
  // Methods
  // ////////////////////////////////////////////////////

  methods: {
    /**
     * Register a section when it's mounted in the form
     * @param   {Vue}  rfSection
     */
    _onSectionAdded (rfSection) {
      // If section specified an index, add it there. Remember, sections are
      // added not only on intial render, but also as a result of v-if becoming
      // true, so the order they are added may not be the order they appear in
      // the DOM, which is why we have the index prop on rf-sections.
      if (rfSection.index !== null) {
        this.sections.splice(rfSection.index, 0, rfSection)
        // If we've just inserted a section before currently active section, we
        // need to update the activeSectionIndex
        if (rfSection.index <= this.activeSectionIndex) {
          this.activeSectionIndex++
        }
      } else {
        this.sections.push(rfSection)
      }

      // If this is the first (or only) section, activate
      if (this.sections.length === 1) {
        rfSection.activate()
        this.activeSectionIndex = rfSection.index || 0
      }
    },

    /**
     * Navigate to the next section
     * @param   {Vue}  sectionVm
     * @param   {Object}  e The event that triggered the navigation (could be a
     * click on rf-section-nav or a history popstate)
     */
    _onSectionNavNext (sectionVm, e) {
      // Activate next section if it exists
      let forceShowUiValidation = true
      this.activeSection.validate(forceShowUiValidation).then((validationErrorCount) => {
        if (!validationErrorCount) {
          this.activeSection.complete()
          var previousSection = this.activeSection
          if (this.activeSectionIndex + 1 < this.sections.length) {
            this.activeSectionIndex++
            this.activeSection.activate()
            // Only if this nav isn't result of popstate!
            if (e.type !== 'popstate') {
              this._pushState(this.activeSectionIndex)
            }
            // Scroll to next section and focus first field (after DOM updated)
            this.$nextTick(function () {
              // Only scroll to if the now previous section is not paginated (otherwise
              // we'll scroll to the wrong place, since the section will be in the
              // middle of the pagination transition)
              if (!previousSection.paginated) {
                this.$scrollTo(this.activeSection.$el, 500, { easing: 'ease-out', offset: this.activeSection.normalizedScrollOffset })
              }
              this.activeSection.focusFirstField()
            })
          }
        }
      })
    },

    /**
     * Navigate to the previous section
     * @param   {Vue}  sectionVm
     * @param   {Object}  e The event that triggered the navigation
     */
    _onSectionNavPrev (sectionVm, e) {
      let prevSection = this.activeSection
      if (this.activeSectionIndex > 0) {
        this.activeSectionIndex--
        this.activeSection.activate()
        prevSection.deactivate()
        // Scroll to prev section and focus first field (after DOM updated)
        this.$nextTick(function () {
          if (!this.activeSection.paginated) {
            this.$scrollTo(this.activeSection.$el, 500, { easing: 'ease-out', offset: this.activeSection.normalizedScrollOffset })
          }
          this.activeSection.focusFirstField()
        })
      }
    },

    /**
     * Remove sections
     * @param   {Vue}  sectionVm
     */
    _onSectionDestroyed (sectionVm) {
      // Save this now, because after we filter, this.activeSection may no longer
      // be the correct section
      let currentActiveSectionUid = this.activeSection._uid

      // Keep only sections that did NOT just get destroyed
      this.sections = this.sections.filter(function (section) {
        return section._uid !== sectionVm._uid
      })

      // Restore this.activeSection
      // If the active section was removed (and it wasn't the first section), decrement activeSectionIndex
      if (sectionVm._uid === currentActiveSectionUid._uid && this.activeSectionIndex > 0) {
        this.activeSectionIndex--
      // Otherwise point activeSectionIndex back to the current active section
      } else {
        this.sections.forEach((section, i) => {
          if (section._uid === currentActiveSectionUid) {
            this.activeSectionIndex = i
          }
        })
      }
    },

    /**
    * Push new browser history state
    * @param  {Integer|String} index
    */
    _pushState: function (index) {
      if (typeof history.pushState !== 'undefined') {
        var state = { targetSectionIndex: index }
        var section = parseInt(index, 10) + 1
        history.pushState(state, '', '#s' + section)
      } else {
        // IE9 doesn't support the history API
        window.location.href = window.location.href.replace(/#.*/g, '') + '#s' + section
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/frontend/mixins/form/sections.js